import type { LearningsPageResponse } from '@@/api/learnings/page/page-types';
import type { KeyLearning } from '@/state/learnings/page-view';
import type { LearningsPageManager } from '@/state/learnings/useLearningsPage';
import type { LearningsScreenManager } from '@/state/learnings/useLearningsScreen';
import { Button } from '@/components/ui/button';
import { cn } from 'helpers/cn';
import {
  barGeometry,
  evidenceGroups,
  keyLearnings,
  OPPORTUNITY_KIND_LABELS,
  scaleFor,
  signedPercent,
  signedRange,
  SURPRISE_TAG,
  surpriseTitle,
  TONE_TEXT,
} from '@/state/learnings/page-view';
import { FOCUS_RING, Meter, MetricTile, Panel } from '../../ConsolePanel';
import { EvidenceMap } from './EvidenceMap';
import { IntervalBar, Section, ToneChip } from './parts';

type FeedViewProps = { manager: LearningsPageManager; page: LearningsPageResponse; screen: LearningsScreenManager };

const plural = (count: number, word: string) => `${count} ${count === 1 ? word : `${word}s`}`;

/** The Learnings tab: what the tests add up to, the three things to act on, and every pattern on one axis. */
export const FeedView = ({ manager, page, screen }: FeedViewProps) => (
  <div className="flex flex-col gap-[14px]">
    <Hero page={page} screen={screen} />
    <KeyLearnings page={page} screen={screen} />
    <div className="flex flex-wrap items-start gap-[14px]">
      <div className="min-w-0 flex-[2.4_1_560px]">
        <EvidenceMap
          page={page}
          device={screen.device}
          onDevice={screen.setDevice}
          onOpenPattern={screen.openPattern}
          onOpenTest={screen.openTest}
        />
      </div>
      <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-[14px]">
        <Coverage page={page} />
        <OpenQuestions page={page} onOpen={() => screen.openTab('opportunities')} />
      </div>
    </div>
    {manager.isError && <p className="text-danger m-0 text-meta">Could not load the latest learnings.</p>}
  </div>
);

type HeroProps = { page: LearningsPageResponse; screen: LearningsScreenManager };

/**
 * What the tests add up to. The headline is the wording job's, shown only while it still sums the
 * page up; until it has been written there is a plain one from the figures.
 */
const Hero = ({ page, screen }: HeroProps) => {
  const { status, summary, surprises, opportunities, multiplicity } = page;
  const patterns = evidenceGroups(page.evidence).filter((group) => group.pooled).length;
  const lead = summary?.lead ?? null;
  const leadOpportunity = lead?.kind === 'opportunity' ? opportunities.find((entry) => entry.id === lead.id) : null;

  const primary =
    lead?.kind === 'pattern'
      ? { label: 'Open this learning', onClick: () => screen.openPattern(lead.key) }
      : leadOpportunity
        ? {
            label: leadOpportunity.kind === 'explain-surprise' ? 'Explain the surprise' : 'See the open question',
            onClick: () => screen.openTab('opportunities'),
          }
        : surprises.length > 0
          ? { label: 'Explain the surprise', onClick: () => screen.openTab('opportunities') }
          : null;

  return (
    <section
      className="bg-panel border-line rounded-panel grid overflow-hidden border [grid-template-columns:repeat(auto-fit,minmax(min(100%,300px),1fr))]"
      data-tour="learnings-hero"
    >
      <div className="flex flex-col gap-2.5 p-[18px]">
        <span className="micro-label">What {plural(status.labelled, 'test')} add up to</span>
        <h1 className="m-0 max-w-[30ch] text-[20px] font-medium leading-tight tracking-[-0.015em] text-balance">
          {summary?.headline ?? 'Learnings are taking shape'}
        </h1>
        <p className="text-dim m-0 max-w-[62ch] text-pretty">
          {summary?.body ??
            `${plural(patterns, 'pattern')} from ${plural(status.labelled, 'test')} so far. The headline is written once the next analysis has run.`}
        </p>
        <div className="mt-1 flex flex-wrap gap-2">
          {primary && (
            <Button size="sm" onClick={primary.onClick}>
              {primary.label}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => screen.openTab('evidence')}>
            See all evidence
          </Button>
        </div>
      </div>
      <div className="bg-panel2 border-line flex flex-col gap-2.5 border-l p-3.5">
        <div className="grid grid-cols-2 gap-[7px]">
          <MetricTile
            label="Tests read"
            value={String(status.labelled)}
            subtitle={`of ${status.concluded} concluded`}
          />
          <MetricTile label="Patterns" value={String(patterns)} subtitle={`${multiplicity.selected} conclusive`} />
          <MetricTile
            label="Surprise"
            value={String(surprises.length)}
            valueClassName="text-accent"
            subtitle="won unpredicted"
          />
          <MetricTile
            label="Open questions"
            value={String(opportunities.length)}
            valueClassName="text-warn"
            subtitle="ready to draft"
          />
        </div>
      </div>
    </section>
  );
};

type KeyLearningsProps = { page: LearningsPageResponse; screen: LearningsScreenManager };

const KeyLearnings = ({ page, screen }: KeyLearningsProps) => {
  const picked = keyLearnings(page);
  if (picked.length === 0) return null;
  const scale = scaleFor(
    picked.flatMap((entry) =>
      entry.kind === 'surprise'
        ? [(entry.surprise.lift ?? 0) / 100]
        : [entry.pattern.pooled?.lower ?? 0, entry.pattern.pooled?.upper ?? 0],
    ),
  );

  return (
    <Section
      title="Key learnings"
      description="The things most worth acting on right now."
      tourAnchor="learnings-key"
      actions={<span className="micro-label">Ranked by what you can do next</span>}
    >
      <div className="grid [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
        {picked.map((entry, index) => (
          <KeyColumn key={keyOf(entry)} entry={entry} index={index} scale={scale} screen={screen} page={page} />
        ))}
      </div>
    </Section>
  );
};

const keyOf = (entry: KeyLearning) =>
  entry.kind === 'surprise' ? `surprise:${entry.surprise.experimentId}` : entry.pattern.key;

type KeyColumnProps = {
  entry: KeyLearning;
  index: number;
  scale: ReturnType<typeof scaleFor>;
  screen: LearningsScreenManager;
  page: LearningsPageResponse;
};

const KeyColumn = ({ entry, index, scale, screen, page }: KeyColumnProps) => {
  const isSurprise = entry.kind === 'surprise';
  const { tag, tone } = isSurprise ? SURPRISE_TAG : entry;
  const mean = isSurprise ? (entry.surprise.lift ?? 0) / 100 : (entry.pattern.pooled?.mean ?? 0);
  const lower = isSurprise ? null : (entry.pattern.pooled?.lower ?? null);
  const upper = isSurprise ? null : (entry.pattern.pooled?.upper ?? null);
  const title = isSurprise ? surpriseTitle(entry.surprise) : (entry.pattern.wording?.title ?? entry.pattern.label);
  const body = isSurprise
    ? (page.tests[entry.surprise.experimentId]?.learning ?? 'Find out why before scoring the next idea.')
    : (entry.pattern.wording?.description ?? `${plural(entry.pattern.tests, 'test')} behind it.`);
  const meta = isSurprise
    ? `1 test · ${entry.surprise.outcome}${entry.surprise.concludedAt ? ` · ${new Date(entry.surprise.concludedAt).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}` : ''}`
    : `${plural(entry.pattern.tests, 'test')}${lower !== null && upper !== null ? ` · ${signedRange(lower, upper)}` : ''}`;

  return (
    <div className="border-line flex flex-col gap-2 border-r p-3.5">
      <div className="flex items-center justify-between">
        <span className="micro-label">{String(index + 1).padStart(2, '0')}</span>
        <ToneChip tone={tone}>{tag}</ToneChip>
      </div>
      <span className="text-body font-medium text-balance">{title}</span>
      <span className="supporting flex-1 text-meta text-pretty">{body}</span>
      <div className="mt-1">
        <IntervalBar bar={barGeometry({ mean, lower, upper, scale })} tone={tone} label={signedPercent(mean)} />
      </div>
      <div className="border-line flex justify-between gap-2 border-b pb-2.5">
        <span className={cn('num font-medium', TONE_TEXT[tone])}>{signedPercent(mean)}</span>
        <span className="micro-label tracking-[0.08em]">{meta}</span>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            isSurprise ? screen.openTest(entry.surprise.experimentId) : screen.openPattern(entry.pattern.key)
          }
        >
          Evidence
        </Button>
        <Button size="sm" variant="secondary" onClick={() => screen.openTab('opportunities')}>
          Test this further
        </Button>
      </div>
    </div>
  );
};

type CoverageProps = { page: LearningsPageResponse };

const Coverage = ({ page }: CoverageProps) => {
  const { status, coverage, multiplicity } = page;
  const percent = status.concluded ? Math.round((status.labelled / status.concluded) * 100) : 0;
  const rows = [
    {
      label: 'Patterns found',
      value: evidenceGroups(page.evidence).filter((group) => group.pooled).length,
      warn: false,
    },
    { label: 'Conclusive so far', value: multiplicity.selected, warn: false },
    { label: 'Weak foundation · still counted', value: coverage.weakFoundation, warn: true },
    { label: 'Left out: tracking issue', value: coverage.leftOut.trackingOrRelease, warn: true },
  ];

  return (
    <Panel title="Coverage" tourAnchor="learnings-coverage">
      <div className="flex flex-col gap-1.5">
        <div className="flex justify-between">
          <span className="micro-label text-foreground">
            {status.labelled} / {status.concluded} read
          </span>
          <span className="micro-label">{percent}%</span>
        </div>
        <Meter percent={percent} className="bg-accent" />
        {rows.map((row) => (
          <div key={row.label} className="border-line flex items-center justify-between border-t py-2">
            <span className="text-meta">{row.label}</span>
            <span className={cn('num', row.warn && row.value > 0 ? 'text-warn' : 'text-foreground')}>{row.value}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
};

type OpenQuestionsProps = { page: LearningsPageResponse; onOpen: () => void };

const OpenQuestions = ({ page, onOpen }: OpenQuestionsProps) => (
  <Section
    title="Open questions"
    actions={
      page.opportunities.length > 0 ? (
        <button type="button" onClick={onOpen} className={cn('text-accent text-meta hover:underline', FOCUS_RING)}>
          All {page.opportunities.length}
        </button>
      ) : undefined
    }
  >
    {page.opportunities.slice(0, 3).map((opportunity) => (
      <button
        key={opportunity.id}
        type="button"
        onClick={onOpen}
        className={cn(
          'border-line hover:bg-hover flex w-full flex-col gap-1 border-b px-3.5 py-2.5 text-left transition-colors duration-150',
          FOCUS_RING,
        )}
      >
        <span className="micro-label">{OPPORTUNITY_KIND_LABELS[opportunity.kind]}</span>
        <span className="text-meta font-medium">{opportunity.question}</span>
      </button>
    ))}
    {page.opportunities.length === 0 && (
      <p className="supporting m-0 px-3.5 py-2.5">No open question yet. They appear as the evidence raises them.</p>
    )}
  </Section>
);
